export const IntegrationName = {
  Gmail: 'gmail',
  Calendar: 'calendar',
  /** A Microsoft mailbox (Outlook.com / Microsoft 365), SQ-324. */
  Outlook: 'outlook'
} as const;
export type IntegrationNameType = typeof IntegrationName[keyof typeof IntegrationName];
export const isIntegrationName = (value: string): value is IntegrationNameType => {
  return Object.values(IntegrationName).includes(value as IntegrationNameType);
};

export const IntegrationType = {
  Google: 'google',
  Microsoft: 'microsoft'
} as const;
export type IntegrationTypeType = typeof IntegrationType[keyof typeof IntegrationType];
export const isIntegrationType = (value: string): value is IntegrationTypeType => {
  return Object.values(IntegrationType).includes(value as IntegrationTypeType);
};

/**
 * How an account is connected: Google hands the page an auth code to post
 * (`@react-oauth/google`'s popup flow); Microsoft is the API's own OAuth
 * callback, so the page only opens the URL the API answers and comes back
 * with the result in the query (SQ-324): to the Account page since SQ-743.
 */
export type ConnectFlow = 'google-code' | 'api-callback';

export interface IntegrationInfo {
  name: IntegrationNameType;
  type: IntegrationTypeType;
  displayName: string;
  /** What connecting it does, in the person's words (Android's ConnectedAccounts.kt). */
  blurb: string;
  scopes: string[];
  connectFlow: ConnectFlow;
  /** Gmail and Outlook are both "the company's mailbox": connecting one replaces the other. */
  isMail: boolean;
}

export const googleIntegrationBaseScopes = ['https://www.googleapis.com/auth/userinfo.profile']
export const gmailScopes = ['https://www.googleapis.com/auth/gmail.send', 'https://www.googleapis.com/auth/userinfo.email']
export const googleCalendarScopes = ['https://www.googleapis.com/auth/calendar.app.created']

export const GmailIntegration: IntegrationInfo = {
  name: IntegrationName.Gmail,
  type: IntegrationType.Google,
  displayName: 'Gmail',
  blurb: 'Send estimates and invoices from your address',
  scopes: [...googleIntegrationBaseScopes, ...gmailScopes],
  connectFlow: 'google-code',
  isMail: true,
}

export const OutlookIntegration: IntegrationInfo = {
  name: IntegrationName.Outlook,
  type: IntegrationType.Microsoft,
  displayName: 'Outlook',
  blurb: 'Send estimates and invoices from your Microsoft 365 or Outlook.com mailbox',
  // The API asks Microsoft for the scopes; the page sends none.
  scopes: [],
  connectFlow: 'api-callback',
  isMail: true,
}

export const CalendarIntegration: IntegrationInfo = {
  name: IntegrationName.Calendar,
  type: IntegrationType.Google,
  displayName: 'Calendar',
  // What the web's company Calendar integration does today: one Google
  // account per company, a "Savaquan Managed Calendar" made in it,
  // appointments only (SQ-404 corrected the design's "appointments and jobs
  // on your calendar" to this).
  blurb: "Puts the company's appointments on a Savaquan calendar in your Google account",
  scopes: [...googleIntegrationBaseScopes, ...googleCalendarScopes],
  connectFlow: 'google-code',
  isMail: false,
}

export const IntegrationInfos: IntegrationInfo[] = [
  GmailIntegration,
  OutlookIntegration,
  CalendarIntegration
]

/**
 * The callback's query on the way back to the Account page (the API's
 * CallbackRedirect): `connected=<name>` on success, `connect=<name>` with
 * `connect_error=<reason>` on failure.
 */
export const ConnectResultParams = {
  connected: 'connected',
  attempted: 'connect',
  error: 'connect_error',
} as const;

/** The reasons the API's callback can send back, in the person's words. */
export const connectErrorText = (reason: string, displayName: string): string => {
  switch (reason) {
    case 'access_denied':
      return `${displayName} wasn't connected: the sign-in was cancelled.`;
    case 'admin_consent_required':
      return `${displayName} wasn't connected: your organization lets only its Microsoft 365 administrator approve apps. Ask them to approve SavaQuan, then try again.`;
    case 'no_address':
      return `${displayName} wasn't connected: Microsoft didn't say which mailbox was signed in.`;
    case 'state_invalid':
      return `${displayName} wasn't connected: the request expired. Press Connect again.`;
    default:
      return `${displayName} wasn't connected (${reason}). Try again.`;
  }
};
